import _ from "lodash";
import React from "react";
import { scrollIntoView } from "./ScrollIntoView";
import ACLStore from "lib/aclStore";
import { htmlToText } from "./HTMLHelpers";
import {
  CURRICULUM_TYPE_DP,
  MARKDOWN,
  FETCHING_RECIPIENTS,
  SELECTING_RECIPIENTS,
  CREATING_BANNER,
  CREATING_BANNER_WITHOUT_TOKEN,
} from "Constants/stringConstants";
import {
  generateNestedSelectedData,
  getPYPElementSetFromNodes,
} from "./BenchmarkDataHelper";
import { getJsonItem, setJsonItem } from "services/localStorage";
import {
  CODE_EDITOR_PROGRAMMING_LANGUAGES,
  AI_TUTOR_LEARNING_TOOLS,
} from "@toddle-edu/common-utils";
import update from "immutability-helper";

import {
  CodeOutlined,
  WorkbookColored,
  VideosOutlined,
  UserSpeakingOutlined,
  WorkbookOutlined,
  PageFoldPortraitOutlined,
} from "@toddle-edu/ds-icons";
import { getIsAITutorModalitiesEnabled } from "./ACLStoreUtils";

import showdown from "showdown";

export const SET_TEMPLATE_FIELDS = "SET_TEMPLATE_FIELDS";
export const CODE_EDITOR_MODALITY = "code_editor";
export const TODDLE_DOC_MODALITY = "document";
export const VIDEO_EXPLORER_MODALITY = "video_explorer";
export const WORKBOOK_MODALITY = "create_template_multipage";
export const SEARCH = "SEARCH";
export const RUN_PYTHON_CODE = "RUN_PYTHON_CODE";

export const scrollAndHighlightBackground = params => {
  const {
    id,
    className,
    timeout = 1500,
    highlightChild = false,
    ...rest
  } = params;

  const elements = document.querySelectorAll(`[id="${id}"]`);

  if (elements.length > 0) {
    const targetElements = [];

    _.forEach(elements, element => {
      // always highlight the element itself
      targetElements.push(element);
      element.classList.add(className);

      // additionally highlight child if required
      if (highlightChild && element.firstElementChild) {
        targetElements.push(element.firstElementChild);
        element.firstElementChild.classList.add(className);
      }
    });

    setTimeout(() => {
      _.forEach(targetElements, targetElement => {
        targetElement.classList.remove(className);
      });
    }, timeout);

    scrollIntoView({
      element: _.first(elements),
      block: "start",
      inline: "start",
      ...rest,
    });

    return _.first(elements);
  }

  return null;
};

export const getCommonMarkdownComponents = _.memoize(params => {
  const { anchorClassName } = params || {};

  return {
    hr: () => <p>{"-----"}</p>,
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={anchorClassName}
      >
        {_.first(children)}
      </a>
    ),
  };
}, JSON.stringify);

export const getPromptLabelsObj = params => {
  const {
    t,
    displayText,
    tutorSummary,
    action,
    tutorGoal,
    i18n,
    localeGroup = "aiTutor",
    showLocalisedLabel = true,
  } = params;

  const selectedLanguage = _.get(i18n, "language", "en");
  const isEnglishLanguageSelected = selectedLanguage === "en";

  let isLocalizedLabelMismatch = false;
  let isLocalizedSubtextMismatch = false;
  let isLocalizedGoalMismatch = false;
  if (
    isEnglishLanguageSelected &&
    ACLStore.can("FeatureFlag:EnableAIDisplayTextDiff")
  ) {
    const localisedLabel = t(`${localeGroup}:${action}_display_text`);
    const localisedSubtext = t(`${localeGroup}:${action}_tutor_summary`);
    const localisedTutorGoal = t(`${localeGroup}:${action}_tutor_goal`);
    isLocalizedLabelMismatch = !_.isEqual(localisedLabel, displayText);
    isLocalizedSubtextMismatch = !_.isEqual(localisedSubtext, tutorSummary);
    isLocalizedGoalMismatch = !_.isEqual(localisedTutorGoal, tutorGoal);
  }

  const label =
    i18n?.exists(`${localeGroup}:${action}_display_text`) && showLocalisedLabel
      ? t(`${localeGroup}:${action}_display_text`)
      : displayText;

  const summary =
    i18n?.exists(`${localeGroup}:${action}_tutor_summary`) && showLocalisedLabel
      ? t(`${localeGroup}:${action}_tutor_summary`)
      : tutorSummary;

  const goal =
    i18n?.exists(`${localeGroup}:${action}_tutor_goal`) && showLocalisedLabel
      ? t(`${localeGroup}:${action}_tutor_goal`)
      : tutorGoal;

  return {
    displayText: label,
    tutorSummary: summary,
    tutorGoal: goal,
    isLocalizedLabelMismatch,
    isLocalizedSubtextMismatch,
    isLocalizedGoalMismatch,
  };
};

export const getAssessmentFieldValue = ({ fields, uid }) => {
  const fieldData = _.find(fields, { uid });
  return _.get(fieldData, "value", []) || [];
};

export const getCanGenerateAITutorPreview = params => {
  const {
    assessmentDetails,
    tutorFields = [
      "title",
      "description",
      "openingMessage",
      "aiRole",
      "aiRules",
    ],
  } = params;

  return _.every(tutorFields, field => {
    const value = getAssessmentFieldValue({
      fields: assessmentDetails?.allFields,
      uid: field,
    });

    return !_.isEmpty(_.trim(value));
  });
};

export const convertHtmlToText = value => {
  if (typeof value !== "string") return "";

  return _.trim(
    htmlToText(value, {
      preserveNewlines: false,
      wordwrap: null,
      formatters: {
        br: () => " ",
      },
    })
  );
};

export const findEvaluatedCriteriaId = (criteria, proficiency) => {
  return _.find(criteria, criterion => {
    const _label = convertHtmlToText(criterion?.label);
    const _proficiency = _.trim(proficiency);

    return _label === _proficiency || _.includes(_label, _proficiency);
  })?.id;
};

export const setOverriddenModelInLocalStorage = ({
  overriddenModel: modelKey,
  promptSetField,
}) => {
  if (ACLStore.can("Common:EnableOverrideAIModelOptions")) {
    const selectedModels = getJsonItem("overridenAIModel") || {};
    if (modelKey === "default") {
      _.unset(selectedModels, [promptSetField]);
    } else {
      selectedModels[promptSetField] = modelKey;
    }
    setJsonItem({ key: "overridenAIModel", value: selectedModels });
  }
};

export const getOverriddenModelFromLocalStorage = promptSetField => {
  if (ACLStore.can("Common:EnableOverrideAIModelOptions")) {
    const selectedModels = getJsonItem("overridenAIModel") || {};
    return _.get(selectedModels, promptSetField);
  }
  return undefined;
};

const _buildPlannerNodesString = (nodes, currentContext = "") => {
  _.forEach(nodes, node => {
    const { label, children: childrenNodes, isLeaf } = node || {};

    if (isLeaf) {
      currentContext += `${label}\n`;
    } else {
      if (!_.isEmpty(childrenNodes)) {
        currentContext += `${label}\n`;
        currentContext = _buildPlannerNodesString(
          childrenNodes,
          currentContext
        );
      }
    }
  });

  return currentContext;
};

export const getAssessmentLearningGoalsContext = params => {
  const {
    field = "syllabusContentDP",
    curriculumProgramType,
    assessmentDetails,
  } = params;

  const context = {};

  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_DP: {
      const fieldData = _.find(assessmentDetails?.allFields, { uid: field });

      if (fieldData) {
        const { value, resolvedMinimalTree } = fieldData || {};

        const nodeSet = getPYPElementSetFromNodes({
          nodes: _.get(resolvedMinimalTree, "nodes", []),
        });

        const nestedNodes = generateNestedSelectedData({
          ...(nodeSet || {}),
          value,
        });

        const combinedString = _buildPlannerNodesString(nestedNodes);

        if (!_.isEmpty(_.trim(combinedString))) {
          _.set(context, field, combinedString);
        }
      }
    }
  }

  return context;
};

// input with dollar sign like "I save $15 for 4 weeks, that's $60." is not rendered correctly with katex
// the following function will escape the dollar sign to render the text correctly
// e.g "$20" will be converted to "\$20" and "20$" will be converted to "20\$"
export const escapeCurrencyDollars = input => {
  return _.replace(
    input,
    /(^|[^\\])(\$)(?=\d)|(\d)(\$)(?=[^\\]|$)/g,
    (match, g1, g2, g3, g4) => {
      // Case 1: $ before number → $20
      if (g2) return g1 + "\\$";

      // Case 2: $ after number → 20$
      if (g4) return g3 + "\\$";

      return match;
    }
  );
};

// input with dollar sign like "I save $15 for 4 weeks, that's $60." is not rendered correctly with katex
// the following function will escape the dollar sign to render the text correctly
export const sanitizeUserInput = input => {
  if (getIsAITutorModalitiesEnabled()) return escapeCurrencyDollars(input);
  return _.replace(input, /([^`])\$(?=\S)/g, "$1\\$");
};

/**
 * Cleans malformed LaTeX by removing empty subscripts/superscripts
 * @param {string} latex - LaTeX string to clean
 * @returns {string} - Cleaned LaTeX string
 */
export const cleanMalformedLatex = latex => {
  if (!latex) return latex;

  // Remove subscript/superscript with no content
  // Matches _ or ^ that are not followed by a brace group or alphanumeric character
  let cleaned = latex;

  // Remove _ or ^ followed by nothing or whitespace or another operator
  cleaned = _.replace(cleaned, /_(?=[^a-zA-Z0-9{]|$)/g, "");
  cleaned = _.replace(cleaned, /\^(?=[^a-zA-Z0-9{]|$)/g, "");

  return cleaned;
};

const PRESERVE_STRONG_TAG_CONFIG = {
  formatters: {
    keepStrong: (elem, walk, builder) => {
      builder.addInline("**");
      walk(elem.children, builder);
      builder.addInline("**");
    },
    inlineParagraph: (elem, walk, builder) => {
      const isBrOnlyParagraph =
        elem.children.length === 1 && elem.children[0].name === "br";
      if (isBrOnlyParagraph) {
        builder.addInline("\n");
      } else {
        walk(elem.children, builder);
        builder.addInline("\n");
      }
    },
  },
  tags: {
    strong: { format: "keepStrong" },
    p: { format: "inlineParagraph" },
  },
};

export const convertToHtmlWithNewLines = (
  text,
  { preserveFormattingTags = false } = {}
) => {
  return _.trim(
    htmlToText(text, {
      preserveNewlines: true,
      wordwrap: null,
      ...(preserveFormattingTags ? PRESERVE_STRONG_TAG_CONFIG : {}),
    })
  );
};

export const replaceImgWithInlineLatex = ({
  query,
  attachmentItems,
  preserveHtml = false,
}) => {
  const regex = /<img[^>]*data-latex="([^"]+)"[^>]*>((?:&nbsp;|\s)*)/g;

  // Sanitize user input BEFORE replacing img tags with LaTeX
  const sanitizedQuery = sanitizeUserInput(query);

  let result = _.replace(sanitizedQuery, regex, (_, latex, space) => {
    // Clean malformed LaTeX before wrapping
    const cleanedLatex = cleanMalformedLatex(latex);
    return `$${cleanedLatex}$` + (space || " "); // add space only if it wasn't present
  });

  // Merge consecutive inline math expressions into single expressions
  // This fixes invalid KaTeX like "$\frac{1}{2}$&nbsp;$+$ $\frac{3}{4}$" to "$\frac{1}{2} + \frac{3}{4}$"
  // Only merge across inline whitespace (spaces/tabs/&nbsp;); newlines indicate
  // intentional separation between equations and must be preserved.
  result = _.replace(
    result,
    /\$([^$]+)\$(?:(?:&nbsp;|[ \t])+\$([^$]+)\$)+/g,
    match => {
      // Extract all LaTeX parts from consecutive expressions
      const parts = [];
      const partRegex = /\$([^$]+)\$/g;
      let partMatch;

      while ((partMatch = partRegex.exec(match)) !== null) {
        parts.push(partMatch[1]);
      }

      return `$${_.join(parts, " ")}$`;
    }
  );

  // replace specific non-supported LaTeX
  result = _.replace(
    _.replace(result, /\\imaginaryI/g, " i"),
    /\\exponentialE/g,
    " e"
  );

  const code = _.get(attachmentItems, "CODE");

  // send the code with user query
  if (!_.isEmpty(_.trim(code))) {
    if (result) return `${convertToHtmlWithNewLines(result)} \n ${code}`;
    else return code;
  }

  let converted = convertToHtmlWithNewLines(result, {
    preserveFormattingTags: preserveHtml,
  });

  if (preserveHtml) {
    converted = _.replace(converted, /\n+/g, match => {
      const n = match.length;
      return "\\\n" + " \\\n".repeat(n - 1);
    });
  }
  return converted;
};

export const getJsonToMarkdownCode = codeEditorObj => {
  const { code = "", language = "javascript" } = codeEditorObj || {};
  const languageCode = _.get(
    _.find(CODE_EDITOR_PROGRAMMING_LANGUAGES, { key: language }),
    "key"
  );
  if (code) return `\`\`\`${languageCode}\n${code}\n\`\`\``;
  else return "";
};

export const getInitialCodeEditorObj = ({ input, inputFormat }) => {
  if (inputFormat === MARKDOWN) {
    return getMarkdownToJsonCode(input);
  }
  return input;
};

export const getFormattedCode = ({ codeEditorObj, outputFormat }) => {
  if (outputFormat === MARKDOWN) {
    return getJsonToMarkdownCode(codeEditorObj);
  }
  return codeEditorObj;
};

export const getMarkdownToJsonCode = markdownCode => {
  const pattern = /```(\w+)?\n([\s\S]*?)```/;
  const match = markdownCode?.match(pattern);

  if (match) {
    const languageCode = _.nth(match, 1);
    const language = _.get(
      _.find(CODE_EDITOR_PROGRAMMING_LANGUAGES, { key: languageCode }),
      "key"
    );
    const code = _.trim(_.nth(match, 2));

    return {
      language,
      code,
    };
  }

  return {
    language: "javascript",
    code: "",
  };
};

export const getAITutorModalitiesToolCalls = () => {
  return [
    "create_code_editor_modality",
    "create_toddle_doc_modality",
    "create_workbook_modality",
    "create_video_explorer_modality",
  ];
};

export const getAITutorModalityAttachmentPattern = () => {
  return `%%%TOOL_MODALITY_CARD%%%`;
};

/**
 * Creates a SYSTEM_EVENT_METADATA message for AI conversations
 * @param {Object} params - The parameters for the metadata message
 * @param {string} params.eventType - The type of event (e.g., "VIDEO_TIMESTAMP", "CHECKPOINT_REACHED")
 * @param {Object} params.eventData - The event-specific data to include in the metadata
 * @returns {string} - Formatted SYSTEM_EVENT_METADATA message
 */
export const createSystemEventMetadataMessage = ({ eventType, eventData }) => {
  const formatValue = value => {
    if (_.isPlainObject(value)) {
      return JSON.stringify(value, null, 2);
    }
    return `"${value}"`;
  };

  const eventDataFields = _.chain(eventData)
    .toPairs()
    .map(([key, value]) => `"${key}": ${formatValue(value)}`)
    .join(",\n  ")
    .value();

  return `SYSTEM_EVENT_METADATA (do not echo; use only for routing)
\`\`\`json
{
  "event_type": "${eventType}",
  ${eventDataFields}
}
\`\`\``;
};

export const isLinkUnprocessed = attachment => {
  const { type, metadata, isDeleted = false } = attachment || {};
  const { generateAiTranscript = false, extractedContentDetails } =
    metadata || {};

  return (
    !isDeleted &&
    (type === "LINK" || type === "VIDEO") &&
    generateAiTranscript &&
    _.isEmpty(extractedContentDetails)
  );
};

export const getUnprocessedLinkAttachments = messages => {
  const lastUserMessage = _.get(_.last(messages), "node", {});
  const { authorType, attachments } = lastUserMessage || {};

  if (authorType !== "user") return {};

  const unprocessedLinks = _.filter(attachments, attachment => {
    return isLinkUnprocessed(attachment);
  });

  if (_.isEmpty(unprocessedLinks)) return {};

  return {
    lastUserMessage,
    unprocessedLinks,
  };
};

export const getAITutorToolNamesAndChoices = params => {
  const { selectedLearningTool } = params;
  const { toolName } = selectedLearningTool || {};

  if (!toolName) {
    return {};
  }

  const result = {
    toolNames: [toolName],
  };

  if (ACLStore.can("FeatureFlag:AITutorToolChoice")) {
    _.set(result, "toolChoice", {
      type: "function",
      function: { name: toolName },
    });
  }

  return result;
};

// Merges content of messages with the same parent message id
export const mergeAndFilterAITutorMessages = messages => {
  const result = [];

  _.forEach(messages, message => {
    const parentId = _.get(message, "node.parentMessage.id");

    const existingMessageIndex = _.findIndex(
      result,
      msg => parentId && _.get(msg, "node.parentMessage.id") === parentId
    );

    if (existingMessageIndex !== -1) {
      const existingMessage = result[existingMessageIndex];
      const mergedContent = _.join(
        [
          _.get(existingMessage, "node.content", ""),
          _.get(message, "node.content", ""),
        ],
        " "
      );

      result[existingMessageIndex] = update(existingMessage, {
        node: {
          content: {
            $set: mergedContent,
          },
        },
      });
    } else {
      result.push(message);
    }
  });

  return result;
};

const mergeConsecutiveUserMessages = messages => {
  const result = [];

  _.forEach(messages, current => {
    const prev = _.last(result);

    const isPrevUser = _.get(prev, "node.authorType") === "user";
    const isCurrUser = _.get(current, "node.authorType") === "user";
    const isPrevMeta = _.has(prev, "node.metadata.isMeta");

    if (isPrevUser && isCurrUser && isPrevMeta) {
      // Remove the previous metadata message from result
      result.pop();

      // Clone current message (to preserve its ID) and use prev's content (which already has everything)
      const merged = _.cloneDeep(current);
      merged.node.content = prev.node.content;
      merged.node.metadata = _.assign(
        {},
        prev.node.metadata,
        merged.node.metadata
      );

      result.push(merged);
    } else {
      result.push(_.cloneDeep(current));
    }
  });

  return result;
};

export const getAITutorMessagesForEvaluationV2 = messages => {
  const mergedUserMessages = mergeConsecutiveUserMessages(messages);

  // filter messages by authorType
  const userAndAssistantMsgs = _.filter(mergedUserMessages, message =>
    ["user", "assistant"].includes(message?.node?.authorType)
  );

  // merge content with same parent ID
  const mergedAndFilteredMsgs = mergeAndFilterAITutorMessages(
    userAndAssistantMsgs
  );

  // filter messages based on if content is empty or not
  const messagesForEvaluation = _.reduce(
    mergedAndFilteredMsgs,
    (result, message) => {
      const node = message?.node;
      const metadata = node?.metadata;
      let content = node?.content;

      // if content is empty, check attachments
      if (_.isEmpty(content)) {
        // use audio transcript if available, otherwise use image URL
        const audioTranscripts = metadata?.audioTranscripts;
        content =
          audioTranscripts?.[0] ||
          metadata?.imageUrls?.[0] ||
          metadata?.audioUrls?.[0];
      }

      // include the message only if content exists
      if (!_.isEmpty(content) || !_.isEmpty(node?.attachments)) {
        result.push({ node: { ...node, content } });
      }

      return result;
    },
    []
  );

  // reverse the processed messages
  return _.reverse(messagesForEvaluation);
};

export const getLearningToolIcon = ({ key }) => {
  switch (key) {
    case "code_editor":
      return {
        coloredIcon: (
          <CodeOutlined
            style={{ color: "var(--decorative-foreground-subtle-blue)" }}
          />
        ),
        outlinedIcon: <CodeOutlined />,
      };
    case "document":
      return {
        coloredIcon: (
          <PageFoldPortraitOutlined
            style={{ color: "var(--decorative-foreground-subtle-teal)" }}
          />
        ),
        outlinedIcon: <PageFoldPortraitOutlined />,
      };
    case "create_template_multipage":
      return {
        coloredIcon: <WorkbookColored />,
        outlinedIcon: <WorkbookOutlined />,
      };
    case "video_explorer":
      return {
        coloredIcon: (
          <VideosOutlined
            style={{ color: "var(--decorative-foreground-subtle-pink)" }}
          />
        ),
        outlinedIcon: <VideosOutlined />,
      };
    case "conversation_mode":
      return {
        coloredIcon: (
          <UserSpeakingOutlined
            style={{ color: "var(--decorative-foreground-subtle-violet)" }}
          />
        ),
        outlinedIcon: <UserSpeakingOutlined />,
      };
  }
};

export const checkIfIntendedModalityCreated = ({
  parsedMessages,
  learningTool,
}) => {
  const requiredToolCallName = _.find(AI_TUTOR_LEARNING_TOOLS, {
    key: learningTool,
  })?.toolName;

  let toolCallDetails = {};
  let setTemplateFieldsDetails = {};

  let isModalityToolCalledSuccessfully = false;
  let isSetTemplateFieldsCalledSuccessfully = false;

  // If current learning tool is "Conversation Mode", only check for set_template_fields call - else check for both modality tool call and set_template_fields call
  const onlySetTemplateFieldsNeeded =
    learningTool === "conversation_mode" || _.isNil(requiredToolCallName);

  // Find out tool call and tool response
  _.forEach(parsedMessages, item => {
    const toolCalls =
      _.get(item, "message.toolCalls") ||
      _.get(item, "node.metadata.toolCalls", []);

    _.forEach(toolCalls, toolCall => {
      const calledToolName = _.get(toolCall, "function.name");
      if (calledToolName === requiredToolCallName) {
        toolCallDetails = toolCall;
      }
      if (calledToolName === "set_template_fields") {
        setTemplateFieldsDetails = toolCall;
      }
    });

    const authorType =
      _.get(item, "message.role") || _.get(item, "node.authorType");

    const toolCallId =
      _.get(item, "message.toolCallId") ||
      _.get(item, "node.metadata.toolCallId");

    if (!_.isEmpty(toolCallDetails) && !isModalityToolCalledSuccessfully) {
      // then find appropriate tool Response
      isModalityToolCalledSuccessfully =
        authorType === "tool" && toolCallId === _.get(toolCallDetails, "id");
    }

    if (
      !_.isEmpty(setTemplateFieldsDetails) &&
      !isSetTemplateFieldsCalledSuccessfully
    ) {
      // then find appropriate tool Response
      isSetTemplateFieldsCalledSuccessfully =
        authorType === "tool" &&
        toolCallId === _.get(setTemplateFieldsDetails, "id");
    }

    // break the loop by returning false, if tool call / responses are found.
    if (
      isSetTemplateFieldsCalledSuccessfully &&
      (onlySetTemplateFieldsNeeded || isModalityToolCalledSuccessfully)
    ) {
      return false;
    }
  });

  return (
    isSetTemplateFieldsCalledSuccessfully &&
    (onlySetTemplateFieldsNeeded || isModalityToolCalledSuccessfully)
  );
};

export const AI_TUTOR_TOOL_NAME_STATE_MAPPING = {
  set_template_fields: SET_TEMPLATE_FIELDS,
  update_template_fields: SET_TEMPLATE_FIELDS,
  create_code_editor_modality: CODE_EDITOR_MODALITY,
  create_toddle_doc_modality: TODDLE_DOC_MODALITY,
  create_video_explorer_modality: VIDEO_EXPLORER_MODALITY,
  create_workbook_modality: WORKBOOK_MODALITY,
  search: SEARCH,
  run_python_code: RUN_PYTHON_CODE,
  visual_agent: RUN_PYTHON_CODE,
};

export const isSchoolisationEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableSchoolisations");
};

export const convertMarkdownToHTML = ({ content }) => {
  const converter = new showdown.Converter({
    tables: true,
    // disableForced4SpacesIndentedSublists is used to disable the 4 spaces indentation for lists
    // setting this to true allows us to use 2 spaces indentation for lists
    disableForced4SpacesIndentedSublists: true,
  });

  return converter.makeHtml(content);
};

export const ANNOUNCEMENT_TOOL_NAME_STATE_MAPPING = {
  get_recipients: FETCHING_RECIPIENTS,
  set_recipients: SELECTING_RECIPIENTS,
  create_banner: CREATING_BANNER,
  create_banner_without_token: CREATING_BANNER_WITHOUT_TOKEN,
};

export const getLangfuseTraceUrl = ({
  traceId,
  observationId,
  langfuseDetails,
}) => {
  if (
    _.isEmpty(traceId) ||
    _.isEmpty(observationId) ||
    _.isEmpty(langfuseDetails)
  ) {
    return "";
  }

  return `${langfuseDetails.baseUrl}/project/${langfuseDetails.projectId}/traces/${traceId}?observation=${observationId}`;
};

export const AI_SDK_REGION_FLAG_MAP = {
  "ap-southeast-2": "FeatureFlag:UseAiSdkAustralia",
  "cn-north-1": "FeatureFlag:UseAiSdkChina",
  "eu-west-1": "FeatureFlag:UseAiSdkEurope",
  "me-central-1": "FeatureFlag:UseAiSdkMiddleEast",
  "eu-central-1": "FeatureFlag:UseAiSdkMiddleEast",
  "us-east-1": "FeatureFlag:UseAiSdkUSA",
  "ap-southeast-1": "FeatureFlag:UseAiSdkSingapore",
  "ap-east-1": "FeatureFlag:UseAiSdkHongKong",
};
